/**
 * The once-per-device tip that points at the header's sparkle: talking to the
 * log through an AI assistant. It waits until the log has entries (the parent
 * knows the taps first) and never shows in night mode, so it can't get in the
 * way of a 3am bottle. It is a callout, not a dialog: nothing behind it is
 * blocked, and either answer, or opening /assistant any other way, retires it.
 */
import type { BottleUnit } from "@babia/checks";
import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { track } from "../../lib/analytics.ts";
import { exampleBottle } from "../../lib/assistants.ts";
import { Button } from "../ui/button.tsx";

const STORAGE_KEY = "babia.assistantTip";

export function assistantTipSeen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "seen";
  } catch {
    // Storage disabled: never nag on every visit.
    return true;
  }
}

export function markAssistantTipSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, "seen");
  } catch {
    // ignore
  }
}

/** Whether to show the tip now; `retire` hides it for good on this device. */
export function useAssistantTip(ready: boolean): { open: boolean; retire: () => void } {
  const [seen, setSeen] = useState(assistantTipSeen);
  return {
    open: ready && !seen,
    retire: () => {
      markAssistantTipSeen();
      setSeen(true);
    },
  };
}

/**
 * Positioned by the header under its controls; `caretRight` is the distance
 * from the controls' right edge to the middle of the sparkle.
 */
export function AssistantTip({
  caretRight,
  babyName,
  unit,
  onAnswer,
}: {
  caretRight: number;
  /** For the example: the baby whose tab is open, and the household's bottle unit. */
  babyName: string;
  unit: BottleUnit;
  onAnswer: (accepted: boolean) => void;
}) {
  function answer(accepted: boolean) {
    track("assistant tip answered", { accepted });
    onAnswer(accepted);
  }
  return (
    <div
      role="note"
      aria-labelledby="assistant-tip-title"
      className="absolute right-0 top-full z-10 mt-3 w-[min(340px,calc(100vw-40px))] border border-divider border-t-[3px] border-t-accent-2 bg-paper px-4 pt-3 pb-2 shadow-sheet"
    >
      <span
        aria-hidden="true"
        className="absolute -top-[9px] size-3 rotate-45 bg-accent-2"
        style={{ right: Math.max(12, caretRight - 6) }}
      />
      <h2 id="assistant-tip-title" className="heading flex items-center gap-2 text-[18px]">
        <Sparkles className="size-[18px] shrink-0 text-accent-2" aria-hidden="true" />
        Talk to your log
      </h2>
      <p className="mt-1.5 text-[15px] leading-snug">
        Tell ChatGPT, Gemini, or Claude “{babyName} had{" "}
        {exampleBottle(unit).big.replace(" ", "\u00a0")} at 2” and it’s logged here, like a tap.
        Setup takes a couple of minutes.
      </p>
      <div className="mt-2 flex items-center gap-1">
        <Link
          to="/assistant"
          onClick={() => answer(true)}
          className="primary-btn heading inline-flex min-h-[44px] items-center px-4 text-[15px]"
        >
          Show me how
        </Link>
        <Button variant="ghost" className="ml-auto text-[14px]" onClick={() => answer(false)}>
          Not now
        </Button>
      </div>
    </div>
  );
}
